import React, { useCallback,useEffect } from "react";
import { toast } from "react-hot-toast";
import { useFormik } from "formik";
import { X } from "lucide-react";
import { z } from "zod";

import styles from "./ChangeStatusModal.module.css";

const statusOptions = [
  { value: "NEW", label: "New" },
  { value: "REFERRED", label: "Referred" },
  { value: "IN_REVIEW", label: "In Review" },
  { value: "REVIEWED", label: "Reviewed" },
  { value: "SYSTEM_GENERATED", label: "System Generated" },
];

const statusSchema = z.object({
  status: z.string().min(1, "Please select a status"),
});

const validateWithZod = (schema) => (values) => {
  try {
    schema.parse(values);
    return {};
  } catch (error) {
    return error.errors.reduce((acc, err) => {
      acc[err.path[0]] = err.message;
      return acc;
    }, {});
  }
};

export const ChangeStatusModal = ({ isOpen, onClose, document }) => {
  const handleSubmit = useCallback(
    async (values) => {
      if (values.status === document?.status) {
        toast("Status is already set to this value");
        onClose();
        return;
      }

      toast.error(
        "Change status functionality is not yet available. API under development."
      );
      onClose();
    },
    [document?.status, onClose]
  );

  const formik = useFormik({
    initialValues: {
      status: document?.status || "",
    },
    validate: validateWithZod(statusSchema),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (isOpen && document) {
      formik.setFieldValue("status", document.status || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, document]);

  const handleClose = useCallback(() => {
    formik.resetForm();
    onClose();
  }, [formik, onClose]);

  if (!isOpen) return null;

  const showError = (fieldName) =>
    (formik.touched[fieldName] || formik.submitCount > 0) &&
    formik.errors[fieldName];

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Change Status</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className={styles.content}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Status</label>
              <select
                className={`${styles.select} ${
                  showError("status") ? styles.inputError : ""
                }`}
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Select status</option>
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              {showError("status") && (
                <span className={styles.errorText}>{formik.errors.status}</span>
              )}
            </div>
          </div>

          <div className={styles.footer}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.saveButton}
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
